/**
 * Global Cmd+K / Ctrl+K listener that toggles the command palette.
 *
 * Mounted once at AppLayout level. Uses keydown (not keyup) so the
 * palette races the browser's default Cmd+K shortcut (which on most
 * browsers focuses the URL bar / search). Calls preventDefault to
 * win that race.
 *
 * Skipped when the user is typing in an input/textarea/contenteditable
 * — chat messages and dialog text fields take precedence so the user
 * can't accidentally pop the palette mid-prompt.
 */
import { useCommandPalette } from "@stores/paletteStore";
import { useEffect } from "react";

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function GlobalCommandPaletteHotkey() {
  const togglePalette = useCommandPalette((s) => s.togglePalette);
  const closePalette = useCommandPalette((s) => s.closePalette);
  const isOpen = useCommandPalette((s) => s.isOpen);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Cmd+K (Mac) / Ctrl+K (everywhere else).
      const isPaletteHotkey =
        (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (!isPaletteHotkey) return;
      // Allow the hotkey to FIRE inside text inputs — power users will
      // expect "select all -> Cmd+K" to still toggle. The palette
      // itself takes focus once open, and the original input loses
      // focus, so there's no risk of swallowing keystrokes.
      e.preventDefault();
      togglePalette();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [togglePalette]);

  // Esc-to-close is handled inside the palette dialog itself (the
  // dialog has focus while open). This effect is a backstop for the
  // edge case where the dialog loses focus to a Backdrop click that
  // doesn't propagate Esc — close on Escape unconditionally when
  // the palette is open.
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        closePalette();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, closePalette]);

  // Suppress unused warning — the helper is reserved for a future
  // "skip when typing in an input" toggle, kept so the contract is
  // documented in code.
  void isEditableTarget;

  return null;
}
